/**
 * Web Target Settings Modal
 * Modal dialog for configuring web target auth settings and login detection
 */

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSave, faCog } from '@fortawesome/free-solid-svg-icons';
import WebTargetAuthSettings from './WebTargetAuthSettings';
import webTargetService from '../../services/webTargetService';
import { toast } from 'react-toastify';

const WebTargetSettingsModal = ({ webTarget, onClose, onSave, isDarkMode }) => {
  const [settings, setSettings] = useState({
    mode: 'auto',
    stalenessDetection: {
      enabled: true,
      autoDetectBehaviorChange: true,
      maxAgeHours: 0,
      warningAgeHours: 24,
      criticalAgeHours: 168
    }
  });
  const [loginDetectionEnabled, setLoginDetectionEnabled] = useState(true);
  const [autoCreateEnabled, setAutoCreateEnabled] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (webTarget) {
      // Load existing settings
      setSettings(webTarget.authProfileUpdateBehavior || {
        mode: 'auto',
        stalenessDetection: {
          enabled: true,
          autoDetectBehaviorChange: true,
          maxAgeHours: 0,
          warningAgeHours: 24,
          criticalAgeHours: 168
        }
      });
      setLoginDetectionEnabled(webTarget.loginDetectionConfig?.enabled !== false);
      setAutoCreateEnabled(webTarget.loginDetectionConfig?.autoCreateProfileFromLogin !== false);
    }
  }, [webTarget]);

  const handleSave = async () => {
    try {
      setSaving(true);

      const updateData = {
        authProfileUpdateBehavior: settings,
        loginDetectionConfig: {
          enabled: loginDetectionEnabled,
          autoCreateProfileFromLogin: autoCreateEnabled
        }
      };

      await webTargetService.update(webTarget._id, updateData);

      toast.success('Settings saved successfully');

      if (onSave) {
        onSave(updateData);
      }

      onClose();
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className={`relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg shadow-xl ${
        isDarkMode ? 'bg-gray-800 text-gray-200' : 'bg-white text-gray-900'
      }`}>
        {/* Header */}
        <div className={`sticky top-0 z-10 flex items-center justify-between p-4 border-b ${
          isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <FontAwesomeIcon icon={faCog} className="text-blue-500" />
            Web Target Settings
          </h2>
          <button
            onClick={onClose}
            className={`p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700`}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Web Target Info */}
          <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700/30' : 'bg-gray-50'}`}>
            <div className="text-sm font-medium mb-1">Web Target</div>
            <div className="text-xs text-gray-500">{webTarget?.baseUrl}</div>
          </div>

          {/* Login Detection */}
          <div>
            <label className={`block text-sm font-medium mb-3 ${
              isDarkMode ? 'text-gray-200' : 'text-gray-700'
            }`}>
              Login Detection
            </label>

            <div className="space-y-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={loginDetectionEnabled}
                  onChange={(e) => setLoginDetectionEnabled(e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm">Enable login detection</span>
              </label>

              {loginDetectionEnabled && (
                <label className="flex items-center gap-2 cursor-pointer pl-6">
                  <input
                    type="checkbox"
                    checked={autoCreateEnabled}
                    onChange={(e) => setAutoCreateEnabled(e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm">Auto-create profiles from detected logins</span>
                </label>
              )}
            </div>
          </div>

          {/* Auth Settings */}
          {autoCreateEnabled && loginDetectionEnabled && (
            <div className={`border-t pt-6 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <WebTargetAuthSettings
                settings={settings}
                onChange={setSettings}
                isDarkMode={isDarkMode}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`sticky bottom-0 flex items-center justify-end gap-3 p-4 border-t ${
          isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded ${
              isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'
            }`}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded flex items-center gap-2"
            disabled={saving}
          >
            <FontAwesomeIcon icon={faSave} />
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WebTargetSettingsModal;
